import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Page } from "@csa/web-foundation";
import { usePartyApi, usePartyAuth } from "../api/party-api";
import type { ConsentRow, GrantConsentInput, Notice } from "../api/contracts";

// The consent surface hooks (TF2, party realm). Reads are plain @PartyOnly self-scope
// queries; grant/withdraw are guarded by fresh-auth (stepUp) BEFORE the mutation — KVKK
// explicit consent must not ride a stale session (ADR 0005 §5). Both invalidate the
// consents query on success so the ledger reflects the new state.
const CONSENTS_KEY = ["customer", "consents"];
const NOTICES_KEY = ["customer", "notices"];

/** A user-abandoned step-up (popup closed) — a benign cancel, not a real error. The UI
 *  swallows it (no grant/withdraw happened); nothing to surface. */
export class FreshAuthAbandoned extends Error {
  constructor() {
    super("fresh-auth abandoned");
    this.name = "FreshAuthAbandoned";
  }
}

/** GET /v1/customer/notices — the versioned aydınlatma notices for the customer's org. */
export function useNotices() {
  const api = usePartyApi();
  return useQuery({
    queryKey: NOTICES_KEY,
    queryFn: () => api.get<Page<Notice>>("/v1/customer/notices"),
  });
}

/** GET /v1/customer/consents — the party's own consent grants (self scope via RLS). */
export function useConsents() {
  const api = usePartyApi();
  return useQuery({
    queryKey: CONSENTS_KEY,
    queryFn: () => api.get<Page<ConsentRow>>("/v1/customer/consents"),
  });
}

/** POST /v1/parties/:partyId/consents — grant per (purpose × channel). Fresh-auth first;
 *  `partyId` comes from the party principal's masked profile. */
export function useGrantConsent(partyId: string) {
  const api = usePartyApi();
  const auth = usePartyAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: GrantConsentInput) => {
      const fresh = await auth.stepUp(); // KVKK: re-auth (auth_time ≤ 300s) BEFORE the grant
      if (!fresh) throw new FreshAuthAbandoned();
      return api.post(`/v1/parties/${partyId}/consents`, input);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: CONSENTS_KEY }),
  });
}

/** POST /v1/customer/consents/:id/withdraw — withdraw is as easy as granting, and equally
 *  fresh-auth-gated (symmetric; ADR 0005 §5). */
export function useWithdrawConsent() {
  const api = usePartyApi();
  const auth = usePartyAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (consentId: string) => {
      const fresh = await auth.stepUp(); // same protection on the way out
      if (!fresh) throw new FreshAuthAbandoned();
      return api.post(`/v1/customer/consents/${consentId}/withdraw`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: CONSENTS_KEY }),
  });
}
